import type { PaginatedResponse, QueryParams } from '@/api/services/base.service'
import apiClient from '@/api/client'
import type {
  BonusAccess,
  BonusImportResult,
  BonusLock,
  BudgetTeamOverview,
  SalesBonus,
  SalesBonusCreate,
  CompanyEvent,
  CompanyEventCreate,
  CompanyEventSummary,
  ExpenseBudgetRole,
  Expense,
  ExpenseCategory,
  ExpenseConfig,
  ExpenseCreate,
  KmReimbursement,
  KmReimbursementCreate,
  MonitoringYear,
} from '@/types/expense.types'

/**
 * API client for the expense suite (feature: expenses.suite). Like the
 * leave service, the generic CRUD surface doesn't fit (cancel/decision
 * transitions, multipart uploads), so every call is explicit.
 */
class ExpenseApiService {
  async getCategories(): Promise<ExpenseCategory[]> {
    const resp = await apiClient.get<ExpenseCategory[]>('/expense/categories')
    return resp.data
  }

  async getExpenses(params?: QueryParams): Promise<PaginatedResponse<Expense>> {
    const resp = await apiClient.get<PaginatedResponse<Expense>>('/expense/requests', { params })
    return resp.data
  }

  async createExpense(data: ExpenseCreate): Promise<Expense> {
    const resp = await apiClient.post<Expense>('/expense/requests', data)
    return resp.data
  }

  async getExpense(id: number): Promise<Expense> {
    const resp = await apiClient.get<Expense>(`/expense/requests/${id}`)
    return resp.data
  }

  async updateExpense(id: number, data: Partial<ExpenseCreate>): Promise<Expense> {
    const resp = await apiClient.patch<Expense>(`/expense/requests/${id}`, data)
    return resp.data
  }

  async cancelExpense(id: number): Promise<Expense> {
    const resp = await apiClient.post<Expense>(`/expense/requests/${id}/cancel`)
    return resp.data
  }

  async decideExpense(id: number, approve: boolean): Promise<Expense> {
    const resp = await apiClient.post<Expense>(`/expense/requests/${id}/decision`, {
      status: approve ? 'expense.status_approved' : 'expense.status_rejected',
    })
    return resp.data
  }

  async uploadDocument(expenseId: number, file: File): Promise<Expense> {
    const form = new FormData()
    form.append('file', file)
    const resp = await apiClient.post<Expense>(`/expense/requests/${expenseId}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return resp.data
  }

  async getKmList(params?: QueryParams): Promise<PaginatedResponse<KmReimbursement>> {
    const resp = await apiClient.get<PaginatedResponse<KmReimbursement>>('/expense/km', { params })
    return resp.data
  }

  async createKm(data: KmReimbursementCreate): Promise<KmReimbursement> {
    const resp = await apiClient.post<KmReimbursement>('/expense/km', data)
    return resp.data
  }

  async getKm(id: number): Promise<KmReimbursement> {
    const resp = await apiClient.get<KmReimbursement>(`/expense/km/${id}`)
    return resp.data
  }

  async updateKm(id: number, data: Partial<KmReimbursementCreate>): Promise<KmReimbursement> {
    const resp = await apiClient.patch<KmReimbursement>(`/expense/km/${id}`, data)
    return resp.data
  }

  async cancelKm(id: number): Promise<KmReimbursement> {
    const resp = await apiClient.post<KmReimbursement>(`/expense/km/${id}/cancel`)
    return resp.data
  }

  async decideKm(id: number, approve: boolean): Promise<KmReimbursement> {
    const resp = await apiClient.post<KmReimbursement>(`/expense/km/${id}/decision`, {
      status: approve ? 'expense.status_approved' : 'expense.status_rejected',
    })
    return resp.data
  }

  async getKmRate(): Promise<string> {
    const resp = await apiClient.get<{ km_rate: string }>('/expense/km/rate')
    return resp.data.km_rate
  }

  async getEvents(): Promise<CompanyEvent[]> {
    const resp = await apiClient.get<CompanyEvent[]>('/expense/events')
    return resp.data
  }

  async getBonusAccess(): Promise<BonusAccess> {
    const resp = await apiClient.get<BonusAccess>('/expense/bonuses/access')
    return resp.data
  }

  async getBonuses(params?: QueryParams): Promise<SalesBonus[]> {
    const resp = await apiClient.get<SalesBonus[]>('/expense/bonuses', { params })
    return resp.data
  }

  /** Months whose payroll bonus file was generated (bonuses frozen); all years when omitted. */
  async getBonusLocks(year?: number): Promise<BonusLock[]> {
    const resp = await apiClient.get<{ months: BonusLock[] }>('/expense/bonuses/locks', {
      params: year === undefined ? undefined : { year },
    })
    return resp.data.months
  }

  async getMyBonuses(): Promise<SalesBonus[]> {
    const resp = await apiClient.get<SalesBonus[]>('/expense/bonuses/my')
    return resp.data
  }

  async getTeamBonuses(): Promise<SalesBonus[]> {
    const resp = await apiClient.get<SalesBonus[]>('/expense/bonuses/team')
    return resp.data
  }

  async createBonus(data: SalesBonusCreate): Promise<SalesBonus> {
    const resp = await apiClient.post<SalesBonus>('/expense/bonuses', data)
    return resp.data
  }

  async deleteBonus(id: number): Promise<void> {
    await apiClient.delete(`/expense/bonuses/${id}`)
  }

  async getBonusEntryRoles(): Promise<number[]> {
    const resp = await apiClient.get<{ role_ids: number[] }>('/expense/bonus-entry-roles')
    return resp.data.role_ids
  }

  async setBonusEntryRoles(roleIds: number[]): Promise<void> {
    await apiClient.put('/expense/bonus-entry-roles', { role_ids: roleIds })
  }

  async getBonusEligibleRoles(): Promise<number[]> {
    const resp = await apiClient.get<{ role_ids: number[] }>('/expense/bonus-eligible-roles')
    return resp.data.role_ids
  }

  async setBonusEligibleRoles(roleIds: number[]): Promise<void> {
    await apiClient.put('/expense/bonus-eligible-roles', { role_ids: roleIds })
  }

  async importBonuses(file: File): Promise<BonusImportResult> {
    const form = new FormData()
    form.append('file', file)
    const resp = await apiClient.post<BonusImportResult>('/expense/bonuses/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return resp.data
  }

  async getEventsSummary(): Promise<CompanyEventSummary[]> {
    const resp = await apiClient.get<CompanyEventSummary[]>('/expense/events/summary')
    return resp.data
  }

  async createEvent(data: CompanyEventCreate): Promise<CompanyEvent> {
    const resp = await apiClient.post<CompanyEvent>('/expense/events', data)
    return resp.data
  }

  async updateEvent(id: number, data: Partial<CompanyEventCreate>): Promise<CompanyEvent> {
    const resp = await apiClient.patch<CompanyEvent>(`/expense/events/${id}`, data)
    return resp.data
  }

  async deleteEvent(id: number): Promise<void> {
    await apiClient.delete(`/expense/events/${id}`)
  }

  async getMonitoring(
    year: number,
    leaderId?: number,
    authUserId?: number,
  ): Promise<MonitoringYear> {
    const resp = await apiClient.get<MonitoringYear>('/expense/monitoring', {
      params: { year, leader_id: leaderId, auth_user_id: authUserId },
    })
    return resp.data
  }

  // ── P&C administration ─────────────────────────────────────────────

  async getConfig(): Promise<ExpenseConfig> {
    const resp = await apiClient.get<ExpenseConfig>('/expense/config')
    return resp.data
  }

  async updateConfig(
    data: Partial<
      Pick<
        ExpenseConfig,
        'km_rate' | 'km_max_distance' | 'meal_voucher_value' | 'meal_voucher_min_hours'
      >
    >,
  ): Promise<ExpenseConfig> {
    const resp = await apiClient.patch<ExpenseConfig>('/expense/config', data)
    return resp.data
  }

  async getBudgetRoles(): Promise<ExpenseBudgetRole[]> {
    const resp = await apiClient.get<ExpenseBudgetRole[]>('/expense/budget/roles')
    return resp.data
  }

  async setBudgetRole(roleId: number, annualAmount: string): Promise<void> {
    await apiClient.put(`/expense/budget/roles/${roleId}`, { annual_amount: annualAmount })
  }

  async getBudgetTeam(year?: number): Promise<BudgetTeamOverview> {
    const resp = await apiClient.get<BudgetTeamOverview>('/expense/budget/team', {
      params: year ? { year } : undefined,
    })
    return resp.data
  }

  async updateCategory(key: string, data: Partial<ExpenseCategory>): Promise<ExpenseCategory> {
    const resp = await apiClient.patch<ExpenseCategory>(`/expense/categories/${key}`, data)
    return resp.data
  }
}

export const expenseService = new ExpenseApiService()
